// ─────────────────────────────────────────────────────────────────────────────
//  KARRIERE-SEITE & STELLEN-BANNER – KONFIGURATION
//
//  Hier wird die Stellenanzeige unter /karriere gepflegt sowie das schmale
//  Banner, das oben auf allen Seiten auf die Stelle hinweist. Es muss NUR
//  diese Datei bearbeitet werden – die Komponenten bleiben unveraendert.
//
//  Schnellanleitung:
//   1. `banner.enabled` auf false setzen, wenn der Hinweis oben verschwinden
//      soll. `page.enabled` auf false schaltet die Seite auf "Stelle bereits
//      besetzt" um (die URL bleibt erreichbar).
//   2. Bei einer NEUEN Stelle die `banner.id` aendern (z.B. "poolmonteur-2027"
//      → "poolmonteur-2028"). Dadurch sehen auch Besucher, die das alte Banner
//      weggeklickt hatten, den neuen Hinweis wieder.
//   3. Bilder in `static/img/` ablegen und eintragen: `hero.stageImage` fuer
//      das grosse Foto der Buehne, `hero.poster` fuer das Werbebild daneben.
//   4. Texte unten anpassen. In den Fliesstexten und Listenpunkten sind
//      einfache HTML-Tags erlaubt (z.B. <strong>, <a href="...">).
// ─────────────────────────────────────────────────────────────────────────────

export interface CareerSection {
  /** Ueberschrift des Blocks, z.B. "Deine Aufgaben". */
  title: string;
  /** Listenpunkte. Einfaches HTML erlaubt (<strong> etc.). */
  items: string[];
  /** true = Block wird als grosses dunkles Panel ueber die volle Breite
   *  ausgespielt. Gedacht fuer das Angebot an die Bewerber – also den Grund,
   *  sich ueberhaupt zu melden. Sinnvollerweise nur bei genau einem Block. */
  highlight?: boolean;
}

export interface CareerConfig {
  /** Schmales Hinweis-Banner ganz oben auf allen Seiten. */
  banner: {
    /** true = Banner wird angezeigt, false = komplett aus. */
    enabled: boolean;
    /** Eindeutige ID. Bei neuer Stelle aendern, damit das Banner auch bei
     *  Besuchern erneut erscheint, die es zuvor weggeklickt haben. */
    id: string;
    /** Erster Textteil, immer sichtbar (z.B. "Wir suchen Verstaerkung"). */
    label: string;
    /** Zweiter Textteil, erst ab Tablet sichtbar (z.B. die Stelle). */
    role: string;
    /** Handlungsaufforderung ganz rechts. */
    cta: string;
  };

  page: {
    /** false = Seite zeigt statt der Anzeige einen Hinweis, dass aktuell
     *  keine Stelle offen ist (Initiativbewerbung bleibt moeglich). */
    enabled: boolean;
    /** Text, der bei `enabled: false` angezeigt wird. */
    closedNotice: string;
  };

  metaTitle: string;
  metaDescription: string;

  hero: {
    /** Grossflaechiges Buehnenbild ganz oben, relativ zu /static. */
    stageImage: string;
    /** Hauptueberschrift (h1) auf der Buehne. */
    title: string;
    /** Stellenbezeichnung, erscheint unter der Ueberschrift auf der Buehne. */
    role: string;
    /** Werbebild des Kunden, relativ zu /static. */
    poster: string;
    posterAlt: string;
    /** Bildunterschrift unter dem Werbebild. */
    posterCaption: string;
    /** Kleiner Text ueber der Einstiegsfrage. */
    eyebrow: string;
    /** Fettgesetzte Einstiegsfrage. */
    lead: string;
    /** Fliesstext-Absaetze. HTML erlaubt. */
    intro: string[];
    /** Beschriftung des Buttons, der zum Formular springt. */
    ctaLabel: string;
  };

  /** Eckdaten-Leiste unter dem Hero. */
  facts: { label: string; value: string }[];

  /** Hinweis-Block auf der Seite "Ueber uns". */
  teaser: {
    /** false = kein Hinweis auf "Ueber uns". */
    enabled: boolean;
    eyebrow: string;
    title: string;
    /** Fliesstext. HTML erlaubt. */
    text: string;
    ctaLabel: string;
  };

  /** Strukturierte Daten fuer Google Jobs (schema.org/JobPosting). */
  jobPosting: {
    /** Stellenbezeichnung. */
    title: string;
    /** ISO-Datum der Veroeffentlichung, z.B. "2026-09-04". */
    datePosted: string;
    /** ISO-Datum, bis zu dem die Anzeige gilt. */
    validThrough: string;
    /** schema.org-Wert, z.B. "FULL_TIME". */
    employmentType: string;
  };

  /** Aufgaben / Angebot / Anforderungen. */
  sections: CareerSection[];

  /** Hinweis unter den Bloecken (z.B. Quereinsteiger). HTML erlaubt. */
  note: string;

  closing: {
    title: string;
    /** Fliesstext-Absaetze. HTML erlaubt. */
    text: string[];
  };

  contact: {
    phone: string;
    /** Telefonnummer in Link-Schreibweise, z.B. "+4951127131450". */
    phoneHref: string;
    mail: string;
  };

  form: {
    eyebrow: string;
    title: string;
    text: string;
    /** Wird als Anliegen fest mitgeschickt – im Postfach klar erkennbar. */
    category: string;
  };
}

export const career: CareerConfig = {
  banner: {
    enabled: true,
    id: "poolmonteur-2027",
    label: "Wir suchen Verstärkung",
    role: "Poolmonteur (m/w/d) für 2027",
    cta: "Jetzt bewerben",
  },

  page: {
    enabled: true,
    closedNotice:
      "Aktuell ist keine Stelle ausgeschrieben. Über eine Initiativbewerbung " +
      "freuen wir uns trotzdem – schreiben Sie uns einfach.",
  },

  metaTitle:
    "Karriere bei Heinze Pool – Poolmonteur (m/w/d) für 2027 in Hannover",
  metaDescription:
    "Poolmonteur (m/w/d) gesucht ab 2027 bei Heinze Pool in Isernhagen bei Hannover ✓ 37,5 Stunden ✓ faire Vergütung ✓ abwechslungsreiche Projekte ✓ familiäres Team. Jetzt bewerben!",

  hero: {
    stageImage: "/img/bauphase_kran.jpg",
    title: "Wir suchen Verstärkung",
    role: "Poolmonteur (m/w/d) – ab 2027",
    poster: "/img/karriere-poolmonteur-2027.jpg",
    posterAlt:
      "Stellenanzeige Heinze Pool – Wir suchen Verstärkung: Poolmonteur (m/w/d) für 2027",
    posterCaption: "Unsere Anzeige – gern weitersagen.",
    eyebrow: "Karriere bei Heinze Pool",
    lead: "Du willst nicht jeden Tag dasselbe machen?",
    intro: [
      "Dann komm zu <strong>HeinzePool</strong> und werde Teil unseres Teams!",
      "Wir suchen ab <strong>2027 einen Poolmonteur (m/w/d)</strong>, der Lust " +
        "auf abwechslungsreiche Projekte, selbstständiges Arbeiten und " +
        "hochwertige Poolanlagen hat.",
      "Bei uns baust du nicht einfach nur einen Pool – <strong>du erschaffst " +
        "Urlaubsparadiese im Garten unserer Kunden.</strong> Von der Montage " +
        "und Installation bis zur technischen Inbetriebnahme bist du direkt am " +
        "Projekt beteiligt und siehst am Ende des Tages, was du geschaffen hast.",
    ],
    ctaLabel: "Jetzt bewerben",
  },

  facts: [
    { label: "Start", value: "2027" },
    { label: "Arbeitszeit", value: "37,5 Std./Woche" },
    { label: "Standort", value: "Isernhagen bei Hannover" },
    { label: "Vergütung", value: "Fair & leistungsgerecht" },
  ],

  teaser: {
    enabled: true,
    eyebrow: "Karriere",
    title: "Werde Teil unseres Teams",
    text:
      "Unsere Geschichte schreiben Menschen, die anpacken. Ab <strong>2027 " +
      "suchen wir einen Poolmonteur (m/w/d)</strong>, der mit uns " +
      "Urlaubsparadiese in den Gärten unserer Kunden baut – mit fairer " +
      "Vergütung, 37,5 Stunden pro Woche und einem Team, das zusammenhält.",
    ctaLabel: "Zur Stellenanzeige",
  },

  jobPosting: {
    title: "Poolmonteur (m/w/d)",
    datePosted: "2026-09-04",
    validThrough: "2027-12-31",
    employmentType: "FULL_TIME",
  },

  sections: [
    {
      title: "Deine Aufgaben",
      items: [
        "Montage und Installation hochwertiger Poolanlagen",
        "Installation und Verrohrung der Pooltechnik",
        "Montage von Pumpen, Filteranlagen, Dosieranlagen und weiteren Komponenten",
        "Inbetriebnahme und technische Betreuung der Anlagen",
        "Service- und Wartungsarbeiten bei unseren Kunden",
        "Arbeiten auf abwechslungsreichen Baustellen und bei unterschiedlichen Projekten",
        "Eigenständiges Arbeiten und Übernehmen von Verantwortung",
      ],
    },
    {
      // Das Angebot an die Bewerber – bekommt bewusst die groesste Buehne.
      title: "Das erwartet dich bei uns",
      highlight: true,
      items: [
        "<strong>Faire und leistungsgerechte Vergütung</strong>",
        "<strong>37,5 Stunden pro Woche</strong>",
        "<strong>30 Urlaubstage im Jahr</strong>",
        "Abwechslungsreiche und spannende Projekte",
        "<strong>Eigenständiges Arbeiten</strong>",
        "Hochwertige Arbeitskleidung wird gestellt",
        "Ein abwechslungsreicher Arbeitsplatz statt jeden Tag derselben Baustelle",
        "Ein familiäres und kollegiales Team",
        "Moderne Pooltechnik und hochwertige Materialien",
        "Die Möglichkeit, gemeinsam mit uns zu wachsen und dich weiterzuentwickeln",
        "Und vor allem: <strong>Du erschaffst Orte, an denen andere ihren Urlaub verbringen</strong>",
      ],
    },
    {
      title: "Das solltest du mitbringen",
      items: [
        "Handwerkliches Geschick und technisches Verständnis",
        "Idealerweise eine handwerkliche Ausbildung oder Berufserfahrung im technischen Bereich",
        "Zuverlässigkeit und selbstständige Arbeitsweise",
        "Spaß an abwechslungsreichen Aufgaben und praktischer Arbeit",
        "Führerschein Klasse B",
        "Kundenfreundliches Auftreten",
        "Motivation, Verantwortung zu übernehmen und Neues zu lernen",
      ],
    },
  ],

  note:
    "<strong>Quereinsteiger mit handwerklichem Geschick sind ebenfalls willkommen.</strong>",

  closing: {
    title: "Klingt nach deinem nächsten Job?",
    text: [
      "Dann freuen wir uns darauf, dich kennenzulernen!",
      "Komm ins Team und hilf uns dabei, jeden Tag neue Urlaubsparadiese zu erschaffen.",
    ],
  },

  contact: {
    phone: "0511 2713145",
    phoneHref: "+4951127131450",
    mail: "info@heinze-pool.de",
  },

  form: {
    eyebrow: "Bewerbung",
    title: "Bewirb dich in einer Minute",
    text:
      "Schreib uns kurz, wer du bist und was du mitbringst – ein " +
      "ausformulierter Lebenslauf ist für den ersten Kontakt nicht nötig. " +
      "Wir melden uns schnellstmöglich bei dir zurück.",
    category: "Bewerbung",
  },
};
